import type { PreviewServiceCall } from "@/api/previewInsights";
import { resultJson } from "@/lib/labServices";
import { Badge } from "@/ui/Badge";

const MODE_TONES = {
  off: "outline",
  shadow: "warning",
  on: "success",
} as const satisfies Record<
  PreviewServiceCall["mode"],
  "outline" | "warning" | "success"
>;

/**
 * One chat-path service call: name, mode, outcome, latency and the compact
 * result as JSON. Renders ANY service, with no per-service code: a new service
 * shows up here the moment the platform records it. Nothing here is patient text.
 */
export function LabServiceCall({ call }: { call: PreviewServiceCall }) {
  return (
    <div className="space-y-1 border-b border-border pb-2 text-sm last:border-b-0">
      <p className="flex flex-wrap items-center gap-2">
        <span dir="ltr" className="font-medium">
          {call.service}
        </span>
        <Badge tone={MODE_TONES[call.mode]}>{call.mode}</Badge>
        <span dir="ltr" className="font-mono text-xs">
          {call.outcome}
        </span>
        {call.latencyMs !== null && call.latencyMs !== undefined ? (
          <span className="text-xs text-muted-foreground">
            {call.latencyMs} ms
          </span>
        ) : null}
        {call.version !== null && call.version !== undefined ? (
          <span dir="ltr" className="font-mono text-xs text-muted-foreground">
            {call.version}
          </span>
        ) : null}
      </p>
      <details>
        <summary className="cursor-pointer text-xs text-muted-foreground focus-visible:outline-2 focus-visible:outline-ring">
          Result
        </summary>
        <pre
          dir="ltr"
          className="mt-1 max-h-60 overflow-auto rounded-md bg-muted/40 p-2 text-xs"
        >
          {resultJson(call.result)}
        </pre>
      </details>
    </div>
  );
}

/** The calls of one turn, or an explicit empty state. */
export function LabServicesPanel({
  calls,
}: {
  calls: readonly PreviewServiceCall[];
}) {
  if (calls.length === 0) {
    return (
      <p role="status" className="text-sm text-muted-foreground">
        No service calls this turn.
      </p>
    );
  }
  return (
    <div className="space-y-2">
      {calls.map((call) => (
        <LabServiceCall key={`${call.service}`} call={call} />
      ))}
    </div>
  );
}
